import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import ApartmentCard from "@/components/ApartmentCard";
import SectionHeading from "@/components/SectionHeading";

const FilterGroup = ({ label, options, value, onChange, testid }) => (
  <div className="flex flex-col gap-2">
    <span className="overline text-muted-foreground text-[10px]">{label}</span>
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => onChange(value === o.value ? (typeof o.value === "number" ? 0 : "") : o.value)}
          className={`text-sm px-3 py-1.5 rounded-sm border transition-all ${
            value === o.value
              ? "bg-petrol text-white border-petrol"
              : "bg-transparent text-petrol/70 border-line hover:border-petrol/40"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  </div>
);

export default function Apartments() {
  const { t } = useLang();
  const [apartments, setApartments] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState("");
  const [guests, setGuests] = useState(0);
  const [amenity, setAmenity] = useState("");

  const fetchData = () => {
    api.get("/apartments").then((r) => setApartments(r.data)).finally(() => setLoading(false));
    api.get("/apartments/cities").then((r) => setCities(r.data)).catch(() => {});
  };

  useEffect(() => {
    fetchData();
  }, []);

  const amenities = useMemo(() => {
    const set = new Set();
    apartments.forEach((a) => a.amenities?.forEach((x) => set.add(x)));
    return [...set];
  }, [apartments]);

  const filtered = apartments.filter((a) => {
    if (city && a.city !== city) return false;
    if (guests && a.max_guests < guests) return false;
    if (amenity && !a.amenities?.includes(amenity)) return false;
    return true;
  });

  return (
    <div data-testid="apartments-page" className="pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionHeading overline={t("apartments.overline")} title={t("apartments.title")} subtitle={t("apartments.subtitle")} />

        {/* Filters */}
        <div className="mt-12 mb-14 flex flex-col lg:flex-row gap-8 lg:gap-14 border-y border-line py-8">
          {cities.length > 0 && (
            <FilterGroup
              label={t("apartments.filter_city")}
              testid="filter-city"
              value={city}
              onChange={setCity}
              options={cities.map((c) => ({ value: c, label: c }))}
            />
          )}
          <FilterGroup
            label={t("apartments.filter_guests")}
            testid="filter-guests"
            value={guests}
            onChange={setGuests}
            options={[2, 3, 4, 5].map((g) => ({ value: g, label: `${g}+` }))}
          />
          {amenities.length > 0 && (
            <FilterGroup
              label={t("apartments.filter_amenity")}
              testid="filter-amenity"
              value={amenity}
              onChange={setAmenity}
              options={amenities.slice(0, 6).map((a) => ({ value: a, label: a }))}
            />
          )}
        </div>

        {loading ? (
          <p className="text-muted-foreground">{t("common.loading")}</p>
        ) : filtered.length === 0 ? (
          <p className="text-muted-foreground py-12" data-testid="no-apartments">{t("apartments.none")}</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="apartments-grid">
            {filtered.map((apt, i) => (
              <ApartmentCard key={apt.id} apt={apt} index={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
